import React, { useEffect, useRef, useState } from "react";
import api from "../../utils/api";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  empty,
  getTranslation,
  showCurrencySymbol,
  showTranslatedValue,
} from "../../helper/generalHelper";
import { useStatuses } from "../../hooks/useStatuses";
import { useSubcategories } from "../../hooks/useSubcategories";

const PAYMENT_COMPLETED = 17;
const ORDER_COMPLETED = 10;

const EditOrder = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { subcategories } = useSubcategories();

  const isStaff = window.location.pathname.includes("/restaurant-staff/");
  const apiPrefix = isStaff ? "restaurant-staff" : "restaurant";
  const navigatePrefix = isStaff ? "restaurant-staff/employee" : "restaurant";

  const { statuses } = useStatuses();
  const [menus, setMenus] = useState([]);
  const [cartQty, setCartQty] = useState({}); // { menuId: { qty, ...itemData } }
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState(null);

  const [orderNote, setOrderNote] = useState("");
  const [extraTaxValue, setExtraTaxValue] = useState("");
  const [extraDiscountType, setExtraDiscountType] = useState("fixed");
  const [extraDiscountValue, setExtraDiscountValue] = useState("");
  const [otherCostValue, setOtherCostValue] = useState("");
  const [otherCostLabel, setOtherCostLabel] = useState("");

  const [customerName, setCustomerName] = useState("");
  const [customerMobile, setCustomerMobile] = useState("");
  const [customerEmail, setCustomerEmail] = useState("");
  const [orderDate, setOrderDate] = useState("");
  const [orderTime, setOrderTime] = useState("");

  const [orderStatus, setOrderStatus] = useState(1);
  const [paymentStatus, setPaymentStatus] = useState(18);
  const [paymentMethod, setPaymentMethod] = useState("");
  const prevPaymentStatus = useRef(paymentStatus);
  const prevOrderStatus = useRef(orderStatus);

  const itemsPerPage = 8;
  const [currentPage, setCurrentPage] = useState(1);

  const userDatas = JSON.parse(localStorage.getItem("userData")) || {};
  const restaurantId = userDatas?.restaurantId;
  const [orderType, setOrderType] = useState("");
  const [tableNumber, setTableNumber] = useState("");
  const [tableName, setTableName] = useState("");

  const { id: urlOrderId } = useParams();

  // ✅ Fetch existing order
  const fetchOrder = async (id) => {
    try {
      setLoading(true);
      const res = await api.get(`${apiPrefix}/order/get?id=${id}`);

      const order = res.data;

      setOrderType(order.order_type);
      setTableNumber(order.table_number);
      setTableName(order.table_name);
      setOrderNote(order.note || "");
      setExtraTaxValue(order.extra_tax_amout !== null && Number(order.extra_tax_amout) !== 0 ? Number(order.extra_tax_amout) : "");
      setExtraDiscountType(order.extra_discount_type || "fixed");
      setExtraDiscountValue(order.extra_discount !== null && Number(order.extra_discount) !== 0 ? Number(order.extra_discount) : "");
      setOtherCostValue(order.other_cost !== null && Number(order.other_cost) !== 0 ? Number(order.other_cost) : "");
      setOtherCostLabel(order.other_cost_type || "");
      setOrderStatus(order.order_status || 10);
      setPaymentStatus(order.payment_status || 16);
      setPaymentMethod(order.payment_mode || "");
      setCustomerName(order.customer_name || "");
      setCustomerMobile(order.customer_mobile || "");
      setCustomerEmail(order.customer_email || "");
      setOrderDate(order.order_date || new Date().toISOString().split("T")[0]);
      setOrderTime(order.order_time || new Date().toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", second: "2-digit" }));

      // Menus (with qty prefilled)
      const orderItems = (order.order_details || []).map((od) => ({
        id: od.menu_id,
        qty: od.quantity,
      }));

      const response = await api.post(`${apiPrefix}/order/menus`, 
     { category_id: "all", restaurantId },
      );

      // Seed cartQty from existing order items
      const initialCartQty = {};
      orderItems.forEach((oi) => {
        const found = (response.data || []).find((m) => m.id === oi.id);
        if (found) initialCartQty[oi.id] = { ...found, qty: oi.qty };
      });
      setCartQty(initialCartQty);

      setMenus(response.data || []);
      setActiveCategory("all");
      setCurrentPage(1); // Reset page 

    } catch (err) {
      console.error("Error fetching order:", err);
      toast.error("Failed to load order.");
    } finally {
      setLoading(false);
    }
  };
 

  useEffect(() => {
    fetchOrder(urlOrderId);
  }, [urlOrderId]);

  useEffect(() => {
    // Logic for auto-status updates removed as requested
    prevPaymentStatus.current = paymentStatus;
    prevOrderStatus.current = orderStatus;
  }, [paymentStatus, orderStatus]);

  const fetchMenusByCategory = async (categoryId) => {
    try {
      setLoading(true);
      const response = await api.post(`${apiPrefix}/order/menus`, 
   { category_id: categoryId, restaurantId },
      );
      setMenus(response.data || []);
      setActiveCategory(categoryId);
      setCurrentPage(1); // Reset page
    } catch (err) {
      console.error("Error fetching menus by category:", err);
    } finally {
      setLoading(false);
    }
  };

  const cartItems = Object.values(cartQty).filter((m) => m.qty > 0);
 
  const handleProductClick = (productId) => {
    if (!orderType) return alert("Please select Takeaway or Dine-in first.");
    if (orderType === "table" && !tableNumber)
      return alert("Please select a table.");

    const product = menus.find((m) => m.id === productId);
    setCartQty((prev) => {
      const existing = prev[productId];
      return {
        ...prev,
        [productId]: { ...(existing || product), qty: (existing?.qty || 0) + 1 },
      };
    });
  };

  const handleRemoveItem = (productId) => {
    setCartQty((prev) => {
      const existing = prev[productId];
      if (!existing || existing.qty <= 1) {
        const next = { ...prev };
        delete next[productId];
        return next;
      }
      return { ...prev, [productId]: { ...existing, qty: existing.qty - 1 } };
    });
  };

  const getItemCount = (productId) => cartQty[productId]?.qty || 0;

  const mrpTotal = cartItems.reduce(
    (acc, item) => acc + item.mrp * item.qty,
    0
  );

  const subtotal = cartItems.reduce(
    (acc, item) => acc + item.price * item.qty,
    0
  );

  const extraDiscountAmount =
    extraDiscountType === "percentage"
      ? (subtotal * Number(extraDiscountValue)) / 100
      : Number(extraDiscountValue);

  const itemTaxAmount = cartItems.reduce((acc, item) => {
    if (item.has_tax === 1) {
      return acc + (item.price * item.qty * Number(item.tax)) / 100;
    }
    return acc;
  }, 0);

  const extraTaxAmount = (subtotal * Number(extraTaxValue)) / 100;

  const totalTax = itemTaxAmount + extraTaxAmount;
  const total =
    subtotal - extraDiscountAmount + totalTax + Number(otherCostValue);

  const handlePlaceOrder = async () => {
    if (cartItems.length === 0) return alert("Cart is empty.");
    if (paymentStatus != PAYMENT_COMPLETED && !customerName) return toast.error("Customer name is required for unpaid orders.");
    if (paymentStatus === PAYMENT_COMPLETED && !paymentMethod) {
      return toast.error("Please select a payment method.");
    }

    try {
// Token handled by HTTP Only Cookies

      const payload = {
        id: urlOrderId,
        customerName: customerName,
        customerMobile: customerMobile,
        customerEmail: customerEmail,
        orderDate: orderDate || new Date().toISOString().split("T")[0],
        orderTime: orderTime || new Date().toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", second: "2-digit" }),
        orderType: orderType,
        orderStatus: orderStatus,
        paymentStatus: paymentStatus,
        paymentMode: paymentStatus == PAYMENT_COMPLETED ? paymentMethod : "cash",
        tableNumber: (orderType === "table" || orderType === "dinein") ? tableNumber : null,
        extraTax: Number(extraTaxValue) || 0,
        extraDiscountType: extraDiscountType,
        extraDiscount: Number(extraDiscountValue) || 0,
        otherCost: Number(otherCostValue) || 0,
        otherCostType: otherCostLabel || "",
        note: orderNote || "",
        menus: cartItems.map((item) => ({
          menuId: item.id,
          quantity: item.qty,
        })),
      };

      const response = await api.post(`${apiPrefix}/order/update`, payload);

      if (response.success) {
        toast.success(response.message);
        navigate(`/${navigatePrefix}/orders`);
      } else {
        toast.error(response.message);
      }
    } catch (err) {
      console.error("Error placing order:", err.response || err);
      alert("Failed to place order. Try again.");
    }
  };

  // PAGINATION — only paginate the current category's display items
  const totalPages = Math.ceil(menus.length / itemsPerPage);
  const paginatedMenus = menus.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  return (
    <div className="flex flex-col bg-gray-50 relative mb-16 lg:mb-0">
      {/* Sticky Search Bar */}
      <div className="p-4 bg-white border-b sticky top-0 z-20">
        <input
          type="text"
          placeholder="🔍 Search for a dish..."
          className="w-full p-3 rounded-xl border text-sm focus:ring-2 focus:ring-rose-400 outline-none"
        />
      </div>

      {/* Order Info Editable */}
      <div className="px-4 py-3 text-sm text-gray-700 bg-white shadow-sm sticky top-[4rem] z-10 flex flex-wrap items-center gap-4 border-b">
        <div className="flex items-center gap-2">
          <span className="font-medium text-gray-600">Date:</span>
          <input 
             type="date"
             value={orderDate}
             onChange={(e) => setOrderDate(e.target.value)}
             className="border border-gray-300 rounded-md px-2 py-1 text-sm focus:ring-rose-500 focus:border-rose-500 outline-none"
          />
        </div>
        <div className="flex items-center gap-2">
          <span className="font-medium text-gray-600">Time:</span>
          <input 
             type="time"
             step="1"
             value={orderTime}
             onChange={(e) => setOrderTime(e.target.value)}
             className="border border-gray-300 rounded-md px-2 py-1 text-sm focus:ring-rose-500 focus:border-rose-500 outline-none"
          />
        </div>
        <div className="flex items-center gap-2">
          <span className="font-medium text-gray-600">Type:</span>
          <select 
             value={orderType} 
             onChange={(e) => setOrderType(e.target.value)}
             className="border border-gray-300 rounded-md px-2 py-1 text-sm focus:ring-rose-500 focus:border-rose-500 outline-none capitalize"
          >
             <option value="takeaway">Takeaway</option>
             <option value="table">Table</option>
             <option value="qsr">QSR</option>
          </select>
        </div>
        {(orderType === "table") && (
          <div className="flex items-center gap-2">
            <span className="font-medium text-gray-600">Table:</span>
            <input 
              type="text"
              value={tableNumber}
              onChange={(e) => {
                 setTableNumber(e.target.value);
                 setTableName(e.target.value);
              }}
              className="border border-gray-300 rounded-md px-2 py-1 text-sm focus:ring-rose-500 focus:border-rose-500 outline-none w-20"
              placeholder="No."
            />
          </div>
        )}
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden space-y-4 md:space-y-0">
        {/* Category Sidebar */}
        <div className="md:w-full flex-shrink-0 bg-white border-b md:border-b-0 md:border-r overflow-x-auto md:overflow-y-auto flex md:flex-row gap-2 px-2 py-2 whitespace-nowrap shadow-sm">
          <div
            onClick={() => fetchMenusByCategory("all")}
            className={`flex flex-col items-center gap-1 p-2 rounded-lg cursor-pointer transition min-w-[80px] ${
              activeCategory === "all"
                ? "bg-rose-600 text-white"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
            }`}
          >
            <span className="text-center text-xs font-medium">All</span>
          </div>

          {subcategories.map((cat) => (
            <div
              key={cat.id}
              onClick={() => fetchMenusByCategory(cat.id)}
              className={`flex flex-col items-center gap-1 p-2 rounded-lg cursor-pointer transition min-w-[80px] ${
                activeCategory === cat.id
                  ? "bg-rose-600 text-white"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              <span className="text-center text-xs font-medium">
                {/* {cat.translations?.en?.name || "Unnamed"} */}
                {showTranslatedValue(cat, "name")}
              </span>
            </div>
          ))}
        </div>

        {/* Product Grid */}
        <div className="w-full p-4">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {loading
              ? [...Array(8)].map((_, idx) => (
                  <div
                    key={idx}
                    className="animate-pulse bg-gray-200 h-56 rounded-xl"
                  ></div>
                ))
              : (activeCategory === "all"
                  ? menus.filter(product => getItemCount(product.id) > 0)
                  : paginatedMenus
                ).map((product) => (
                  <div
                    key={product.id}
                    onClick={() => handleProductClick(product.id)}
                    className="relative rounded-xl bg-white shadow hover:shadow-lg cursor-pointer transition h-56 flex flex-col"
                  >
                    <img
                      src={product.menuImages?.[0]}
                      alt={product.translations?.en?.title}
                      className="w-full h-28 object-cover rounded-t-xl"
                    />
                    <div className="p-3 flex flex-col flex-1 justify-between">
                      <p
                        className="text-sm font-semibold text-gray-800 truncate"
                        title={product.translations?.en?.title}
                      > 
                        {showTranslatedValue(product, "title")}
                      </p>
                      <p className="text-rose-600 font-bold text-sm mt-1">
                        {showCurrencySymbol(product.price)}
                      </p>
                    </div>
                    {getItemCount(product.id) > 0 && (
                      <span className="absolute top-2 right-2 bg-green-600 text-white text-xs px-2 py-1 rounded-full shadow">
                        x{getItemCount(product.id)}
                      </span>
                    )}
                  </div>
                ))}
          </div>

          {/* Pagination */}
          {activeCategory !== "all" && totalPages > 1 && (
            <div className="mt-6 flex justify-center items-center space-x-2">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="px-3 py-1 text-sm bg-gray-200 rounded hover:bg-gray-300 disabled:opacity-50"
              >
                Prev
              </button>
              {[...Array(totalPages)].map((_, i) => (
                <button
                  key={i}
                  onClick={() => handlePageChange(i + 1)}
                  className={`px-3 py-1 text-sm rounded ${
                    currentPage === i + 1
                      ? "bg-rose-600 text-white"
                      : "bg-gray-100 hover:bg-gray-200"
                  }`}
                >
                  {i + 1}
                </button>
              ))}
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="px-3 py-1 text-sm bg-gray-200 rounded hover:bg-gray-300 disabled:opacity-50"
              >
                Next
              </button>
            </div>
          )}
        </div>

        {/* Cart Section Below */}

        {/* Cart Section Below */}
        <div className="w-full bg-white border-t md:border-l p-4 lg:pe-[5%] overflow-y-auto">
          <h2 className="text-xl font-bold mb-4 text-center">Cart Summary</h2>

          {cartItems.length === 0 ? (
            <p className="text-center text-gray-500">Your cart is empty.</p>
          ) : (
            <div className="space-y-4">
              {cartItems.map((item) => {
                const quantity = item.qty;
                const price = item.price;
                const mrp = item.mrp || price;
                const savedAmount = (mrp - price) * quantity;
                const itemTax =
                  item.has_tax === 1
                    ? (price * quantity * Number(item.tax)) / 100
                    : 0;
                const itemTotal = price * quantity + itemTax;

                return (
                  <div
                    key={item.id}
                    className="flex items-start gap-3 border-b pb-3 pt-2"
                  >
                    {/* Product Image */}
                    <img
                      src={item.menuImages?.[0]}
                      alt={item.translations?.en?.title}
                      className="w-14 h-14 object-cover rounded"
                    />

                    {/* Info Section */}
                    <div className="flex-1">
                      {/* Product Title */}
                      <p className="text-sm font-semibold text-gray-800">
                        {item.translations?.en?.title}
                      </p>

                      {/* Pricing Details */}
                      <div className="text-xs text-gray-600 mt-1 space-y-1">
                        {/* Price and MRP */}
                        <div className="flex items-center gap-2">
                          <span className="line-through text-gray-400">
                            {showCurrencySymbol(mrp)}
                          </span>
                          <span className="text-rose-600 font-medium">
                            {showCurrencySymbol(price)} × {quantity}
                          </span>
                        </div>

                        {/* Savings */}
                        {mrp > price && (
                          <p className="text-green-600">
                            You Saved: {showCurrencySymbol(savedAmount)}
                          </p>
                        )}

                        {/* Tax Info */}
                        {item.has_tax === 1 && (
                          <p className="text-amber-600">
                            Tax ({item.tax}%): {showCurrencySymbol(itemTax)}
                          </p>
                        )}

                        {/* Subtotal for this item */}
                        <p className="text-gray-700 font-medium">
                          Subtotal: {showCurrencySymbol(itemTotal)}
                        </p>
                      </div>
                    </div>

                    {/* Quantity Buttons */}
                    <div className="flex flex-col items-center gap-1">
                      <button
                        onClick={() => handleProductClick(item.id)}
                        className="px-2 py-1 bg-gray-200 rounded text-sm"
                      >
                        +
                      </button>
                      <span>{item.qty}</span>
                      <button
                        onClick={() => handleRemoveItem(item.id)}
                        className="px-2 py-1 bg-gray-200 rounded text-sm"
                      >
                        -
                      </button>
                    </div>
                  </div>
                );
              })}

              {/* Input Fields Below Cart */}
              <div className="pt-4 border-t text-sm space-y-3">
                <div className="space-y-4 mt-6">
                  {/* Order Note */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Order Note
                    </label>
                    <textarea
                      value={orderNote}
                      onChange={(e) => setOrderNote(e.target.value)}
                      rows={2}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-rose-500 focus:border-rose-500 resize-none"
                      placeholder="Add any special instructions..."
                    />
                  </div>

                  {/* Extra Tax */}
                  <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                    <label className="text-sm font-medium text-gray-700 w-32">
                      Tax (%)
                    </label>
                    <input
                      type="number"
                      value={extraTaxValue}
                      onChange={(e) => setExtraTaxValue(e.target.value)}
                      className="w-full sm:w-32 border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-rose-500 focus:border-rose-500"
                      placeholder="0"
                      autoComplete="off"
                    />
                  </div>

                  {/* Discount Section */}
                  <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                    <label className="text-sm font-medium text-gray-700 w-32">
                      Discount
                    </label>
                    <div className="flex gap-2 w-full">
                      <select
                        value={extraDiscountType}
                        onChange={(e) => setExtraDiscountType(e.target.value)}
                        className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-rose-500 focus:border-rose-500 w-1/2 sm:w-36"
                      >
                        <option value="fixed">Fixed</option>
                        <option value="percentage">Percentage</option>
                      </select>
                      <input
                        type="number"
                        value={extraDiscountValue}
                        onChange={(e) => setExtraDiscountValue(e.target.value)}
                        className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-rose-500 focus:border-rose-500 w-full"
                        placeholder="0"
                        autoComplete="off"
                      />
                    </div>
                  </div>

                  {/* Other Cost */}
                  <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                    <label className="text-sm font-medium text-gray-700 w-32">
                      Other Cost
                    </label>
                    <div className="flex gap-2 w-full">
                      <input
                        type="text"
                        placeholder="Label"
                        value={otherCostLabel}
                        onChange={(e) => setOtherCostLabel(e.target.value)}
                        className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-rose-500 focus:border-rose-500 w-full"
                        autoComplete="off"
                      />
                      <input
                        type="number"
                        placeholder={localStorage.getItem("currency") || "INR"}
                        value={otherCostValue}
                        onChange={(e) => setOtherCostValue(e.target.value)}
                        className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-rose-500 focus:border-rose-500 w-28"
                        autoComplete="off"
                      />
                    </div>
                  </div>

                  {/* Customer Details */}
                  <div className="pt-4 border-t space-y-3">
                    <h3 className="text-sm font-semibold text-gray-800">Customer Details</h3>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Customer Name {paymentStatus != PAYMENT_COMPLETED && <span className="text-red-500">*</span>}
                      </label>
                      <input
                        type="text"
                        value={customerName}
                        onChange={(e) => setCustomerName(e.target.value)}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-rose-500 focus:border-rose-500"
                        placeholder="Enter customer name"
                        required
                      />
                    </div>
                    <div className="flex gap-2">
                      <div className="flex-1">
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Mobile Number
                        </label>
                        <input
                          type="tel"
                          value={customerMobile}
                          onChange={(e) => setCustomerMobile(e.target.value)}
                          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-rose-500 focus:border-rose-500"
                          placeholder="Mobile"
                        />
                      </div>
                      <div className="flex-1">
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Email
                        </label>
                        <input
                          type="email"
                          value={customerEmail}
                          onChange={(e) => setCustomerEmail(e.target.value)}
                          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-rose-500 focus:border-rose-500"
                          placeholder="Email"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Order Status */}
                  <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                    <label className="text-sm font-medium text-gray-700 w-32">
                      Order Status
                    </label>
                    <select
                      value={orderStatus}
                      onChange={(e) => setOrderStatus(Number(e.target.value))}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-rose-500 focus:border-rose-500"
                    >
                      {(statuses?.orders || [])
                        .filter(status => [1, 3, 4, 10, Number(orderStatus)].includes(Number(status.statusId)))
                        .map((status) => (
                        <option key={status.statusId} value={status.statusId}>
                          {getTranslation(status.translations)?.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Payment Status */}
                  <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                    <label className="text-sm font-medium text-gray-700 w-32">
                      Payment Status
                    </label>
                    <select
                      value={paymentStatus}
                      onChange={(e) => {
                        const newStatus = Number(e.target.value);
                        setPaymentStatus(newStatus);
                        if (newStatus === PAYMENT_COMPLETED && !paymentMethod) {
                          setPaymentMethod("cash");
                        }
                      }}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-rose-500 focus:border-rose-500"
                    >
                      {(statuses?.payment || []).map((status) => (
                        <option key={status.statusId} value={status.statusId}>
                          {getTranslation(status.translations)?.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  {paymentStatus === PAYMENT_COMPLETED && (
                    <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                      <label className="text-sm font-medium text-gray-700 w-32">
                        Payment Method
                      </label>
                      <select
                        value={paymentMethod}
                        onChange={(e) => setPaymentMethod(e.target.value)}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-rose-500 focus:border-rose-500"
                      >
                        <option value="cash">Cash</option>
                        <option value="upi">UPI</option>
                        <option value="card">Card</option>
                      </select>
                    </div>
                  )}
                </div>

                {/* Totals */}

                {/* Cart Totals Summary */}
                <div className="pt-5 border-t mt-5 text-sm space-y-3">
                  {/* MRP Total */}
                  <div className="flex justify-between text-gray-500">
                    <span>MRP Total</span>
                    <span className="text-primary line-through">
                      {showCurrencySymbol(mrpTotal)}
                    </span>
                  </div>

                  {/* You Saved */}
                  {mrpTotal > subtotal && (
                    <div className="flex justify-between text-emerald-600 font-medium">
                      <span>You Saved</span>
                      <span>-{showCurrencySymbol(mrpTotal - subtotal)}</span>
                    </div>
                  )}

                  {/* Subtotal */}
                  <div className="flex justify-between text-gray-700 font-medium">
                    <span>Subtotal</span>
                    <span>{showCurrencySymbol(subtotal)}</span>
                  </div>

                  {/* Extra Discount */}
                  {extraDiscountAmount > 0 && (
                    <div className="flex justify-between text-gray-600">
                      <span>Discount</span>
                      <span className="text-emerald-600">
                        -{showCurrencySymbol(extraDiscountAmount)}
                      </span>
                    </div>
                  )}

                  {/* Item Tax */}
                  {itemTaxAmount > 0 && (
                    <div className="flex justify-between text-gray-600">
                      <span>Item Tax</span>
                      <span className="text-orange-500">
                        {showCurrencySymbol(itemTaxAmount)}
                      </span>
                    </div>
                  )}

                  {/* Tax */}
                  {extraTaxAmount > 0 && (
                    <div className="flex justify-between text-gray-600">
                      <span>Tax ({extraTaxValue || 0}%)</span>
                      <span className="text-orange-500">
                        {showCurrencySymbol(extraTaxAmount)}
                      </span>
                    </div>
                  )}

                  {/* Other Cost */}
                  {Number(otherCostValue) > 0 && (
                    <div className="flex justify-between text-gray-600">
                      <span>{otherCostLabel || "Other Cost"}</span>
                      <span className="text-orange-500">
                        {showCurrencySymbol(otherCostValue)}
                      </span>
                    </div>
                  )}

                  {/* Divider */}
                  <hr className="my-2 border-gray-200" />

                  {/* Total */}
                  <div className="flex justify-between items-center bg-gray-100 px-3 py-2 rounded-lg text-base font-semibold text-gray-800 border border-gray-200">
                    <span>Total</span>
                    <span>{showCurrencySymbol(total)}</span>
                  </div>

                  {/* CTA Button */}
                  <button
                    onClick={handlePlaceOrder}
                    className="w-full bg-rose-600 hover:bg-rose-700 text-white text-sm font-semibold py-2.5 rounded-lg shadow-md transition-all duration-200"
                  >
                    ✅ Proceed to Checkout
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EditOrder;
